import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, MessageCircle, ShoppingBag, X } from 'lucide-react';
import api from '@/lib/api';
import SafeImg from '@/components/SafeImg';

const won = (n) => `${(Number(n) || 0).toLocaleString('ko-KR')}원`;

/**
 * LiveOrderConfirm — buyer-facing candidate confirmation page.
 *
 * Flow: viewer arrives via a candidate URL (`/live-order/{cid}`) →
 * we render the parsed intent, let them fill in any missing options,
 * then POST /live-order/{cid}/confirm which (a) adds the item to the
 * viewer's cart and (b) marks the candidate confirmed. We then send
 * them to the STANDARD `/checkout` page — no new order plumbing.
 */
const LiveOrderConfirm = ({ user }) => {
  const { candidateId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [selected, setSelected] = useState({});
  const [qty, setQty] = useState(1);

  const load = async () => {
    try {
      const r = await api.get(`/live-order/${candidateId}`);
      setData(r.data);
      setSelected(r.data?.candidate?.selected_options || {});
      setQty(Math.max(1, Number(r.data?.candidate?.quantity || 1)));
    } catch (e) { setError(e?.response?.data?.detail || '주문 후보를 불러오지 못했습니다.'); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [candidateId]);

  const saveIfDirty = async () => {
    if (!dirty) return true;
    try {
      const r = await api.patch(`/live-order/${candidateId}`, { selected_options: selected, quantity: qty });
      setData((d) => ({ ...d, candidate: r.data }));
      setDirty(false);
      return true;
    } catch (e) { toast.error(e?.response?.data?.detail || '옵션 저장 실패'); return false; }
  };

  const confirm = async () => {
    if (!user) {
      toast.error('로그인 후 이용해주세요.');
      navigate('/login?next=' + encodeURIComponent(`/live-order/${candidateId}`));
      return;
    }
    setBusy(true);
    try {
      if (!(await saveIfDirty())) { setBusy(false); return; }
      const r = await api.post(`/live-order/${candidateId}/confirm`);
      toast.success('장바구니에 담았습니다. 결제를 진행하세요.');
      navigate(r.data?.redirect || '/checkout');
    } catch (e) { toast.error(e?.response?.data?.detail || '주문 확인 실패'); }
    finally { setBusy(false); }
  };

  const cancel = async () => {
    if (!window.confirm('이 주문 후보를 취소하시겠습니까?')) return;
    try {
      await api.post(`/live-order/${candidateId}/cancel`);
      toast.success('주문 후보를 취소했습니다.');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '취소 실패'); }
  };

  if (error) return <div className="max-w-md mx-auto p-10 text-center" data-testid="live-order-error">
    <p className="text-lg font-black mb-3">주문 후보를 찾을 수 없습니다</p>
    <p className="text-sm text-gray-500 mb-4">{error}</p>
    <Link to="/" className="text-sm text-[#FF3B30] font-bold">홈으로</Link>
  </div>;
  if (!data) return <div className="max-w-md mx-auto p-10 text-sm text-gray-400 text-center">불러오는 중…</div>;

  const { candidate: c, product: p, broadcast: b, seller: s } = data;
  const status = c.status;
  const opts = p.options || [];
  const finalized = status === 'confirmed' || status === 'cancelled' || status === 'expired';
  const unitPrice = (Number(p.sale_price) || 0) + opts.reduce((acc, g) => {
    const label = selected[g.name];
    if (!label) return acc;
    const v = (g.values || []).find((x) => x.label === label);
    return acc + (Number(v?.extra_price) || 0);
  }, 0);
  const subTotal = unitPrice * (Number(qty) || 1);
  const shipping = 3000;
  const total = subTotal + shipping;
  const missing = opts.map((g) => g.name).filter((n) => !selected[n]);

  const setOpt = (name, value) => { setSelected((o) => ({ ...o, [name]: value })); setDirty(true); };
  const setQ = (v) => { setQty(Math.max(1, Number(v) || 1)); setDirty(true); };

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-8 space-y-5" data-testid="live-order-confirm">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-xs text-gray-500"><ArrowLeft size={14} /> 뒤로</button>

      <div className="rounded-2xl bg-gradient-to-br from-[#FF3B30] to-[#D32F2F] text-white p-4">
        <p className="text-[11px] font-black inline-flex items-center gap-1"><MessageCircle size={12} /> 댓글 주문 확인</p>
        <p className="text-lg font-black mt-1" data-testid="lo-broadcast-title">{b?.title || '라이브 방송'}</p>
        {s?.name && <p className="text-xs opacity-90">판매자 · {s.name}</p>}
      </div>

      <div className="rounded-2xl border border-black/10 dark:border-white/10 p-4 space-y-3" data-testid="lo-product-card">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-full bg-black text-white text-base font-black flex items-center justify-center shrink-0" data-testid="lo-slot">{c.slot_number}</span>
          <SafeImg src={p.image} kind="product" className="w-16 h-16 rounded-lg object-cover" alt="" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold truncate" data-testid="lo-name">{p.name}</p>
            <p className="text-sm font-black text-[#FF3B30]" data-testid="lo-unit-price">{won(unitPrice)}</p>
          </div>
        </div>

        <div className="text-[11px] text-gray-500 border-t border-black/5 dark:border-white/10 pt-2">
          <p>💬 원본 댓글: <span className="font-bold text-black dark:text-white" data-testid="lo-original">"{c.original_comment}"</span></p>
          {c.buyer_name && <p className="mt-0.5">작성자: {c.buyer_name}</p>}
        </div>
      </div>

      {/* Option pickers — only for groups we still need. */}
      {opts.length > 0 && (
        <div className="rounded-2xl border border-black/10 dark:border-white/10 p-4 space-y-3" data-testid="lo-options">
          {opts.map((g) => (
            <div key={g.name}>
              <div className="flex items-center gap-2 mb-2">
                <p className="text-xs font-black">{g.name}</p>
                {!selected[g.name] && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-orange-500/10 text-orange-600 font-bold">필수</span>}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(g.values || []).map((v) => {
                  const isPicked = selected[g.name] === v.label;
                  return (
                    <button key={v.label} onClick={() => !finalized && setOpt(g.name, v.label)} disabled={finalized}
                      className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                        isPicked ? 'bg-[#FF3B30] text-white border-[#FF3B30]' : 'border-black/10 dark:border-white/10 hover:border-[#FF3B30]'
                      } ${finalized ? 'opacity-60 cursor-not-allowed' : ''}`}
                      data-testid={`lo-opt-${g.name}-${v.label}`}>
                      {v.label}{v.extra_price ? ` +${won(v.extra_price)}` : ''}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          <div className="flex items-center gap-2 pt-2">
            <p className="text-xs font-black">수량</p>
            <div className="inline-flex items-center rounded-lg border border-black/10 dark:border-white/10">
              <button disabled={finalized} onClick={() => setQ(qty - 1)} className="px-3 py-1 disabled:opacity-40" data-testid="lo-qty-dec">−</button>
              <span className="px-3 text-sm font-bold" data-testid="lo-qty">{qty}</span>
              <button disabled={finalized} onClick={() => setQ(qty + 1)} className="px-3 py-1 disabled:opacity-40" data-testid="lo-qty-inc">+</button>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-black/10 dark:border-white/10 p-4 text-sm space-y-1">
        <div className="flex justify-between"><span className="text-gray-500">상품금액</span><span className="font-bold" data-testid="lo-subtotal">{won(subTotal)}</span></div>
        <div className="flex justify-between"><span className="text-gray-500">배송비</span><span className="font-bold">{won(shipping)}</span></div>
        <div className="flex justify-between border-t border-black/10 dark:border-white/10 pt-1 mt-1">
          <span className="font-black">예상 결제금액</span>
          <span className="text-lg font-black text-[#FF3B30]" data-testid="lo-total">{won(total)}</span>
        </div>
      </div>

      {finalized ? (
        <div className="rounded-2xl bg-black/5 dark:bg-white/5 p-4 text-center text-xs" data-testid="lo-finalized">
          현재 상태: <span className="font-black">{status === 'confirmed' ? '주문완료' : status === 'cancelled' ? '취소' : '만료'}</span>
          {c.order_id && <p className="text-[10px] text-gray-500 mt-1">order_id: {c.order_id}</p>}
        </div>
      ) : (
        <div className="flex gap-2">
          <button disabled={busy || missing.length > 0} onClick={confirm}
            className="flex-1 py-3 rounded-full bg-[#FF3B30] text-white text-sm font-black disabled:opacity-40 inline-flex items-center justify-center gap-1"
            data-testid="lo-confirm">
            <ShoppingBag size={14} /> {missing.length > 0 ? `옵션 선택 필요 (${missing.length})` : (busy ? '처리 중…' : '주문하기')}
          </button>
          <button onClick={cancel} className="px-4 py-3 rounded-full border border-black/10 dark:border-white/10 text-sm font-bold inline-flex items-center gap-1" data-testid="lo-cancel">
            <X size={14} /> 취소
          </button>
        </div>
      )}
    </div>
  );
};

export default LiveOrderConfirm;
